type ServiceCategory =
  | 'geological'
  | 'infrastructure'
  | 'maritime'
  | 'land-transport'
  | 'housing'
  | 'petroleum';

interface ServiceIconProps {
  slug: string;
  category?: string | null;
  className?: string;
  imageClassName?: string;
}

const ASSET_BASE = import.meta.env.BASE_URL.endsWith('/')
  ? import.meta.env.BASE_URL
  : `${import.meta.env.BASE_URL}/`;

/**
 * Bespoke platform workflows keep stable slugs while their artwork follows the
 * canonical MOEI catalog filename.
 */
const SERVICE_ICON_ALIASES: Readonly<Record<string, string>> = {
  'housing-assistance-reconsideration':
    'reconsideration-for-housing-assistance-decision-request',
  'land-transport-permit-cancel':
    'cancelationcross-off-a-national-transportation-vehicle-permit',
  'certificate-of-competency': 'apply-for-certificate-of-competency-coc',
  'land-transport-permit-replace':
    'replacement-for-a-lostdamaged-permit-for-a-national-transportation-vehicle-permit',
  'pleasure-boat-mortgage-register':
    'issuing-a-registration-for-a-pleasure-boat-mortgage',
  'pleasure-boat-mortgage-delete': 'deleting-mortgage-pleasure-boat',
  'pleasure-boat-registration-replacement':
    'replacement-for-a-lost-or-damaged-pleasure-boat-registration',
  'pleasure-boat-deletion-replacement':
    'replacement-for-a-lost-or-damaged-pleasure-boat-deletion',
  'pleasure-boat-deletion-certificate':
    'issuing-deletion-pleasure-boat-certificate',
  'infrastructure-noc': 'issuing-non-objection-certificate',
  'land-transport-permit-issue':
    'issuing-a-national-transportation-vehicle-permit',
  'land-transport-permit-renewal':
    'renewal-of-a-national-transportation-vehicle-permit',
  'pleasure-boat-registration-issue': 'issuing-pleasure-boat-registration',
  'pleasure-boat-registration-renewal':
    'renewal-of-pleasure-boat-registration',
  'land-transport-permit-transfer':
    'transfer-of-national-land-transport-vehicle-permit-ownership-per-vehicle',
  'land-transport-permit-modify':
    'modify-data-and-information-for-a-national-transportation-vehicle-permit',
  'pleasure-boat-ownership-transfer': 'transfer-ownership-of-pleasure-boat',
  'pleasure-boat-registration-amend':
    'amending-pleasure-boat-registration-data',
};

function normalizeCategory(category: string | null | undefined): ServiceCategory {
  const normalized = category?.trim().toLowerCase().replaceAll('_', '-').replaceAll(' ', '-');
  if (normalized === 'transport' || normalized === 'land-transport') return 'land-transport';
  if (normalized === 'geological') return 'geological';
  if (normalized === 'maritime') return 'maritime';
  if (normalized === 'housing') return 'housing';
  if (normalized === 'petroleum') return 'petroleum';
  return 'infrastructure';
}

export function getServiceIconUrl(slug: string): string {
  const canonicalSlug = SERVICE_ICON_ALIASES[slug] ?? slug;
  return `${ASSET_BASE}icons/services/${encodeURIComponent(canonicalSlug)}.png`;
}

function getCategoryIconUrl(category: ServiceCategory): string {
  return `${ASSET_BASE}icons/categories/${category}.png`;
}

export function ServiceIcon({
  slug,
  category,
  className = 'h-11 w-11 rounded-xl p-1.5',
  imageClassName = 'h-full w-full object-contain',
}: ServiceIconProps) {
  const categorySlug = normalizeCategory(category);
  return (
    <span
      aria-hidden="true"
      className={`service-category-icon grid shrink-0 place-items-center overflow-hidden ${className}`}
      data-service-category={categorySlug}
    >
      <img
        key={`${slug}:${categorySlug}`}
        alt=""
        className={imageClassName}
        decoding="async"
        loading="lazy"
        src={getServiceIconUrl(slug)}
        onError={(event) => {
          const image = event.currentTarget;
          if (image.dataset.fallbackApplied) return;
          image.dataset.fallbackApplied = 'true';
          image.src = getCategoryIconUrl(categorySlug);
        }}
      />
    </span>
  );
}
